// NodeShare.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Utilities
import {
    buildNodeQRCodeFilename,
    buildNodeQRCodeSvgMarkup,
    buildNodeQRCodeTitle,
    buildNodeQRCodeUrl,
    downloadQRCodeSvg,
} from 'utilities/QRCode';

// CSS
import './NodeShare.css';

// Components
import NodeSharePrintModal from './NodeSharePrintModal';
import Button from 'components/button/Button';

/**
 * NodeShare
 *
 * Displays a node's live QR code with download and print-QR actions.
 *
 * @param {object} node - The node object.
 * @param {number} [containerHeight=280] - Minimum height for the QR card layout.
 *
 * @returns {ReactNode} - NodeShare
 */
const NodeShare = ({ node, containerHeight = 280 }) => {

    // ----- STATE ----- //

    const [feedback, setFeedback] = useState('');
    const [printOpen, setPrintOpen] = useState(false);

    // ----- MEMOS ----- //

    const shareUrl = useMemo(() => buildNodeQRCodeUrl(node?.key), [node?.key]);
    const qrCodeTitle = useMemo(() => buildNodeQRCodeTitle(node), [node]);
    const qrCodeFilename = useMemo(() => buildNodeQRCodeFilename(node), [node]);
    const svgMarkup = useMemo(() => buildNodeQRCodeSvgMarkup(shareUrl), [shareUrl]);

    // ----- HANDLERS ----- //

    /**
     * Downloads the QR code as an SVG file.
     */
    const handleDownload = useCallback(() => {
        const didDownload = downloadQRCodeSvg({
            filename: qrCodeFilename,
            svgMarkup,
        });

        setFeedback(didDownload ? 'SVG downloaded.' : 'Download failed.');
    }, [qrCodeFilename, svgMarkup]);

    /**
     * Opens the QR print preview.
     */
    const handlePrintOpen = useCallback(() => {
        setFeedback('');
        setPrintOpen(true);
    }, []);

    if (!node) return null;

    // ----- RENDER ----- //

    return (
        <>
            <div
              className="node-share-container"
              style={{
                minHeight: containerHeight,
              }}
            >

                {/* CARD */}
                <div className="node-share-card">

                    {/* QR SHELL */}
                    <div className="node-share-qr-shell">
                        {/* QR SVG */}
                        <div
                          className="node-share-qr-svg"
                          dangerouslySetInnerHTML={{ __html: svgMarkup }}
                        />
                    </div>

                    {/* META */}
                    <div className="node-share-meta">
                        {/* TITLE */}
                        <div className="node-share-title">
                            {qrCodeTitle}
                        </div>

                        {/* URL 
                        <div className="node-share-url">
                            {shareUrl}
                        </div>
                        */}
                    </div>

                    {/* ACTIONS */}
                    <div className="node-share-actions">

                        <Button
                          flex="1"
                          iconKey="download"
                          label="Download"
                          onClick={handleDownload}
                        />

                        <Button
                          flex="1"
                          iconKey="print"
                          label="Print"
                          onClick={handlePrintOpen}
                        />

                    </div>

                    {/* FEEDBACK 
                    <div className="node-share-feedback">
                        {feedback || 'Generate stickers, labels, or flyers from this live node QR code.'}
                    </div>
                    */}

                </div>

            </div>

            <NodeSharePrintModal
              isOpen={printOpen}
              node={node}
              svgMarkup={svgMarkup}
              onClose={() => setPrintOpen(false)}
            />
        </>
    );
};

// Dev Tools
NodeShare.displayName = 'NodeShare';

export default NodeShare;
